import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { FORM_ACTIVITIES_QUERY } from 'app/lib/workflows/graphql'

// There is no first-class Forms model/list yet, so we derive a "your forms" selector from activity
// history: recent Activity records of type=Form (GraphQL), then each distinct form's title + fields
// from its FormJson record. FormJson is the DEPRECATED REST surface (/api/activities/{id}/Form/{typeId})
// — flagged for a GraphQL forms list. This is history-derived: a form with no submissions won't appear
// until its first one, and the list is bounded to recent submissions.

/** A form the practice has received submissions from, with the fields it collects (its `answers` keys).
 *  `formIds` = the distinct form-DEFINITION ids (`Form.formId`) seen under this title — the stable
 *  identity engine trigger filters match on. `typeId` is just one representative Form record id. */
export type FormOption = { typeId: string; title: string; fields: string[]; formIds: string[] }

type FormJsonRecord = { title?: string; answers?: Record<string, unknown>; formId?: string }
type FormActivityHit = { id: string; typeId?: string | null }

const HISTORY_LIMIT = 100
const MAX_DISTINCT_FORMS = 25

/** Aggregates a form selector from recent Form activities. See the module header for the caveats. */
export function useFormOptions(): { forms: FormOption[]; loading: boolean } {
  const [result] = useQuery({
    query: FORM_ACTIVITIES_QUERY,
    variables: {
      search: `type:Form`,
      pagination: { limit: HISTORY_LIMIT },
      // SortInput.direction is a SortDirection enum (ASC/DESC) — must be uppercase, not "desc".
      sort: { field: `activityDate`, direction: `DESC` },
    },
  })
  const [forms, setForms] = useState<FormOption[]>([])
  const [resolving, setResolving] = useState(false)

  // Distinct forms by typeId, each with a representative activity id to resolve its FormJson.
  const distinct = useMemo(() => {
    const hits = (result.data?.activities?.hits ?? []) as FormActivityHit[]
    const byType = new Map<string, string>()
    for (const hit of hits) {
      if (hit.typeId && !byType.has(hit.typeId)) byType.set(hit.typeId, hit.id)
    }
    return [...byType.entries()].slice(0, MAX_DISTINCT_FORMS).map(([typeId, activityId]) => ({ typeId, activityId }))
  }, [result.data])

  useEffect(() => {
    if (!distinct.length) {
      setForms([])
      return
    }
    let alive = true
    setResolving(true)
    bg(async () => {
      const resolved = await Promise.all(
        distinct.map(async ({ typeId, activityId }): Promise<FormOption> => {
          try {
            const res = await window.fetch(`/api/activities/${activityId}/Form/${typeId}`)
            const json = (await res.json()) as { record?: FormJsonRecord } | FormJsonRecord
            const record = (json as { record?: FormJsonRecord }).record ?? (json as FormJsonRecord)
            return {
              typeId,
              title: record?.title?.trim() || `Untitled form`,
              fields: record?.answers ? Object.keys(record.answers) : [],
              formIds: record?.formId ? [record.formId] : [],
            }
          } catch (err) {
            logError(`Failed to resolve form title`, { typeId, err })
            return { typeId, title: `Form ${typeId.slice(0, 6)}`, fields: [], formIds: [] }
          }
        }),
      )
      if (!alive) return
      // Collapse forms that share a title (the same form across submissions), keeping the richest field
      // set but accumulating EVERY distinct formId seen — the same-titled form can carry different
      // definition ids across sources, and the trigger filter must match all of them.
      const byTitle = new Map<string, FormOption>()
      for (const form of resolved) {
        const existing = byTitle.get(form.title)
        if (!existing) {
          byTitle.set(form.title, form)
        } else {
          const formIds = [...new Set([...existing.formIds, ...form.formIds])]
          byTitle.set(
            form.title,
            form.fields.length > existing.fields.length ? { ...form, formIds } : { ...existing, formIds },
          )
        }
      }
      setForms([...byTitle.values()].sort((a, b) => a.title.localeCompare(b.title)))
      setResolving(false)
    })
    return () => {
      alive = false
    }
  }, [distinct])

  return { forms, loading: result.fetching || resolving }
}
